import { Link, router } from '@inertiajs/react'
import { Activity, AlertTriangle, Gauge, Settings2, UserX } from 'lucide-react'
import CommandCenterLayout from '@/frontend/layouts/CommandCenterLayout'
import EvalDateFilter, { windowLabel, windowParams } from '@/frontend/components/Eval/EvalDateFilter'
import EvalHelp from '@/frontend/components/Eval/EvalHelp'

interface SparkPoint {
  day: string
  problems: number
  judged: number
}

interface WorkerRow {
  id: string
  name: string
  instanceName: string
  organization: string | null
  machine: string | null
  judged: number
  problems: number
  grave: number
  problemRate: number
  lastJudgedAt: string | null
  spark: SparkPoint[]
}

interface ProblemGroup {
  id: string
  title: string
  rootCause: string | null
  scope: string | null
  recency: string | null
  lastDay: string | null
  occurrences: number
  sessions: number
  instances: Array<{ name: string; count: number; last: string }>
  examples: Array<{ instance: string; day: string; text: string }>
  fixedNote: string | null
}

interface Props {
  windowDays: number | null
  windowFrom: string | null
  windowTo: string | null
  judgeVersion: string
  configuredJudgeVersion: string | null
  configuredCount: number
  judgeVersions: Array<{ version: string; count: number }>
  totals: { judged: number; problems: number; masked: number; needed_human: number }
  workers: WorkerRow[]
  problemGroups: ProblemGroup[]
  ingestRuns: Array<{ machine: string; at: string; stats: Record<string, unknown> }>
  settingsConfigured: boolean
}

const RECENCY_STYLE: Record<string, string> = {
  ativo: 'bg-red-600 text-white',
  esfriando: 'bg-orange-500 text-white',
  legado: 'bg-zinc-400 text-white',
}

function Spark({ points }: { points: SparkPoint[] }) {
  if (!points.length) return <span className="text-xs text-muted-foreground">—</span>
  const max = Math.max(...points.map((p) => p.judged), 1)
  return (
    <div className="flex h-6 items-end gap-px" title="sessões/dia (vermelho = com problema)">
      {points.slice(-14).map((p) => (
        <div key={p.day} className="flex w-1.5 flex-col justify-end" style={{ height: '100%' }}>
          <div
            className="w-full rounded-sm bg-red-500"
            style={{ height: `${(p.problems / max) * 100}%` }}
          />
          <div
            className="w-full rounded-sm bg-zinc-300 dark:bg-zinc-600"
            style={{ height: `${((p.judged - p.problems) / max) * 100}%` }}
          />
        </div>
      ))}
    </div>
  )
}

function Kpi({ icon: Icon, value, label, tone, hint }: { icon: typeof Activity; value: string | number; label: string; tone?: 'bad' | 'warn'; hint?: string }) {
  return (
    <div className={`rounded-lg border bg-card p-4 ${hint ? 'cursor-help' : ''}`} title={hint}>
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className="h-4 w-4" />
        <span className="text-[11px] font-medium uppercase tracking-wide">{label}</span>
      </div>
      <div className={`mt-1 text-2xl font-semibold ${tone === 'bad' ? 'text-red-600' : tone === 'warn' ? 'text-orange-500' : ''}`}>
        {value}
      </div>
    </div>
  )
}

export default function Index({ windowDays, windowFrom, windowTo, judgeVersion, configuredJudgeVersion, configuredCount, judgeVersions, totals, workers, problemGroups, ingestRuns, settingsConfigured }: Props) {
  const pct = (a: number, b: number) => (b > 0 ? `${Math.round((100 * a) / b)}%` : '–')
  const win = { days: windowDays, from: windowFrom, to: windowTo }
  const nav = (params: Record<string, string | number | undefined>) =>
    router.get('/cc/eval', { judge_version: judgeVersion, ...params }, { preserveState: true })

  return (
    <div className="h-full overflow-y-auto">
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Eval contínuo da frota</h1>
          <p className="text-sm text-muted-foreground">
            Sessões de cliente julgadas pelos workers nas máquinas · {windowLabel(win)} · juiz {judgeVersion}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            className="rounded-md border bg-background px-2 py-1.5 text-sm"
            value={judgeVersion}
            title="Série de julgamento (judge_version) — vereditos de juízes diferentes ficam em séries separadas"
            onChange={(e) => router.get('/cc/eval', { judge_version: e.target.value, ...windowParams(win) }, { preserveState: true })}
          >
            {judgeVersions.map((v) => (
              <option key={v.version} value={v.version}>{v.version} ({v.count})</option>
            ))}
          </select>
          <EvalDateFilter window={win} onChange={nav} />
          <Link href="/cc/eval/settings" className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm hover:bg-accent">
            <Settings2 className="h-4 w-4" /> Configuração
          </Link>
        </div>
      </div>

      {!settingsConfigured && (
        <div className="rounded-lg border border-orange-300 bg-orange-50 p-3 text-sm text-orange-900 dark:bg-orange-950/30 dark:text-orange-200">
          Workers da frota estão <b>parados</b>: sem keys de provider (ou <code>enabled: false</code>). Defina as config
          vars <code>EVAL_MINIMAX_API_KEYS</code>/<code>EVAL_OPENAI_API_KEYS</code> no Heroku ou cole keys em{' '}
          <Link href="/cc/eval/settings" className="underline">Configuração</Link>.
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi icon={Activity} value={totals.judged} label="sessões de cliente julgadas"
             hint="Sessões user/qa julgadas na janela. System/boot são amostradas e ficam fora destes KPIs." />
        <Kpi icon={AlertTriangle} value={`${totals.problems} (${pct(totals.problems, totals.judged)})`} label="com problema" tone="bad"
             hint="Não resolvida/abandonada, cliente frustrado/irritado, severidade ≥ major, sucesso falso, erro hard de tool ou segurança ≥ major." />
        <Kpi icon={Gauge} value={totals.masked} label="sucesso falso" tone="warn"
             hint="Agente declarou sucesso ao cliente, mas os outputs das tools mostram falha. Pode coexistir com desfecho 'Resolvida': o desfecho é a versão do cliente; este número é a auditoria técnica." />
        <Kpi icon={UserX} value={totals.needed_human} label="precisou de humano" tone="warn"
             hint="A tarefa só terminou (ou terminaria) com intervenção humana — pergunta-base do sz-eval." />
      </div>

      {configuredJudgeVersion && configuredJudgeVersion !== judgeVersion && (
        <div className="rounded-lg border border-blue-300 bg-blue-50 p-3 text-sm text-blue-900 dark:bg-blue-950/30 dark:text-blue-200">
          A série do juiz novo (<code>{configuredJudgeVersion}</code>) está em repopulação — <b>{configuredCount}</b> vereditos
          até agora. Mostrando <code>{judgeVersion}</code> enquanto isso;{' '}
          <button className="underline" onClick={() => router.get('/cc/eval', { judge_version: configuredJudgeVersion })}>
            ver {configuredJudgeVersion}
          </button>.
        </div>
      )}

      <EvalHelp />

      <section>
        <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
          Por colaborador digital (ordenado por taxa de problema)
        </h2>
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2">colaborador</th>
                <th className="px-3 py-2">org · máquina</th>
                <th className="px-3 py-2 text-right">julgadas</th>
                <th className="px-3 py-2 text-right">problemas</th>
                <th className="px-3 py-2 text-right">graves</th>
                <th className="px-3 py-2">tendência</th>
                <th className="px-3 py-2">última</th>
              </tr>
            </thead>
            <tbody>
              {workers.map((w) => (
                <tr key={w.id} className="border-t hover:bg-accent/40">
                  <td className="px-3 py-2">
                    <Link href={`/cc/eval/${w.id}`} className="font-medium text-primary hover:underline">
                      {w.instanceName}
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{w.organization} · {w.machine}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{w.judged}</td>
                  <td className={`px-3 py-2 text-right tabular-nums ${w.problemRate > 0.4 ? 'font-semibold text-red-600' : ''}`}>
                    {w.problems} ({pct(w.problems, w.judged)})
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{w.grave}</td>
                  <td className="px-3 py-2"><Spark points={w.spark} /></td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {w.lastJudgedAt ? new Date(w.lastJudgedAt).toLocaleString('pt-BR') : '—'}
                  </td>
                </tr>
              ))}
              {workers.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">
                  Nenhum veredito na janela — verifique se os workers fleet-eval estão rodando nas máquinas.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
          Problemas compartilhados na frota (semântico, semanal)
        </h2>
        <div className="space-y-2">
          {problemGroups.map((g) => (
            <details key={g.id} className="rounded-lg border bg-card">
              <summary className="flex cursor-pointer flex-wrap items-center gap-2 px-3 py-2">
                <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${RECENCY_STYLE[g.recency ?? ''] ?? 'bg-zinc-300'}`}>
                  {g.recency ?? '—'}
                </span>
                <span className="flex-1 text-sm font-medium">{g.title}</span>
                <span className="text-xs text-muted-foreground">
                  {g.instances.length} instâncias · {g.occurrences} ocorr · últ. {g.lastDay ?? '—'} · {g.rootCause}
                </span>
              </summary>
              <div className="space-y-2 border-t px-3 py-2 text-sm text-muted-foreground">
                {g.fixedNote && (
                  <div className="rounded border-l-4 border-emerald-500 bg-emerald-50 px-2 py-1 text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200">
                    ✔ {g.fixedNote}
                  </div>
                )}
                <div className="text-xs">
                  instâncias: {g.instances.map((i) => `${i.name} (${i.count})`).join(', ')}
                </div>
                {g.examples.map((ex, i) => (
                  <blockquote key={i} className="border-l-2 pl-2 text-xs">
                    <b>[{ex.instance} · {ex.day}]</b> {ex.text}
                  </blockquote>
                ))}
              </div>
            </details>
          ))}
          {problemGroups.length === 0 && (
            <p className="text-sm text-muted-foreground">Sem grupos computados ainda (job semanal do worker designado).</p>
          )}
        </div>
      </section>

      <section>
        <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">Últimos ciclos dos workers</h2>
        <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
          {ingestRuns.map((r, i) => (
            <span key={i} className="rounded border px-2 py-1">
              {r.machine} · {new Date(r.at).toLocaleString('pt-BR')} · {String((r.stats as { sessions_judged?: number })?.sessions_judged ?? '?')} julgadas
            </span>
          ))}
          {ingestRuns.length === 0 && <span>nenhum ciclo registrado</span>}
        </div>
      </section>
    </div>
    </div>
  )
}

Index.layout = (page: React.ReactElement) => <CommandCenterLayout variant="fullscreen">{page}</CommandCenterLayout>
